import React, { useEffect, useState } from 'react';
import {
  Callout,
  type CommerceSelectOptionViewModel,
  DescriptionText,
  HorizontalStack,
  Surface,
  VerticalStack,
  WizardWorkspace,
} from '@workspace/silk-ui';
import { Cable, CheckCircle2, Info, Users } from 'lucide-react';
import type { StorefrontExperienceReadinessIssue } from '@/lib/commerce';

export type StorefrontEditorMode = 'create' | 'edit';

export type StorefrontFormState = {
  name: string;
  experienceKey: string;
  segment: string;
  eligibilityMode: string;
  routeProfile: string;
  runtimeFamily: string;
  status: string;
  storefrontKey: string;
  channelId: string;
  confirmationRedirectUrl: string;
  autoCreateChannel: boolean;
};

export type StorefrontPrincipalRow = {
  id: string;
  principalType: string;
  principalId: string;
};

export type StorefrontPublicationBindingRow = {
  id: string;
  publicationId: string;
  bindingRole: 'primary' | 'secondary';
  status: 'active' | 'inactive';
  priority: number;
  audience: 'any' | 'public' | 'agent';
  targetType: 'all' | 'market' | 'market_group';
  targetId: string;
};

export interface StorefrontsWizardEditorProps {
  isOpen: boolean;
  mode: StorefrontEditorMode;
  form: StorefrontFormState;
  setForm: React.Dispatch<React.SetStateAction<StorefrontFormState>>;
  principalRows: StorefrontPrincipalRow[];
  onAddPrincipal: () => void;
  onChangePrincipal: (rowId: string, patch: Partial<StorefrontPrincipalRow>) => void;
  onRemovePrincipal: (rowId: string) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
  saving: boolean;
  validationErrors: Record<string, string>;
  channelOptions: CommerceSelectOptionViewModel[];
  bindingRows: StorefrontPublicationBindingRow[];
  bindingPublicationOptions: CommerceSelectOptionViewModel[];
  marketOptions: CommerceSelectOptionViewModel[];
  marketGroupOptions: CommerceSelectOptionViewModel[];
  bindingsLoading: boolean;
  bindingsError?: string | null;
  onAddBinding: () => void;
  onChangeBinding: (rowId: string, patch: Partial<StorefrontPublicationBindingRow>) => void;
  onRemoveBinding: (rowId: string) => void;
  readinessIssues: StorefrontExperienceReadinessIssue[];
  readinessStatus?: string | null;
  dependencyWarnings: string[];
  onManageAccessPoints?: () => void;
  configSnippet: string;
  activeTabId?: string;
  onActiveTabIdChange?: (tabId: string) => void;
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
        {label}
      </label>
      {children}
      {error ? <p className="text-[10px] font-bold text-destructive">{error}</p> : null}
    </div>
  );
}

const inputCls = 'h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';
const selectCls = `${inputCls} cursor-pointer`;

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <Surface className="border-border/60 bg-muted/10 p-4">
      <VerticalStack gap={2}>
        <p className="text-sm font-black tracking-tight text-foreground">{title}</p>
        <DescriptionText className="text-xs">{body}</DescriptionText>
      </VerticalStack>
    </Surface>
  );
}

export function StorefrontsWizardEditor({
  isOpen,
  mode,
  form,
  setForm,
  principalRows,
  onAddPrincipal,
  onChangePrincipal,
  onRemovePrincipal,
  onSave,
  onClose,
  saving,
  validationErrors,
  channelOptions,
  bindingRows,
  bindingPublicationOptions,
  marketOptions,
  marketGroupOptions,
  bindingsLoading,
  bindingsError,
  onAddBinding,
  onChangeBinding,
  onRemoveBinding,
  readinessIssues,
  readinessStatus,
  dependencyWarnings,
  onManageAccessPoints,
  configSnippet,
  activeTabId: controlledActiveTabId,
  onActiveTabIdChange,
}: StorefrontsWizardEditorProps) {
  const [activeTabId, setActiveTabId] = useState(controlledActiveTabId || 'identity');

  useEffect(() => {
    if (controlledActiveTabId) {
      setActiveTabId(controlledActiveTabId);
    }
  }, [controlledActiveTabId]);

  const tabs = [
    { id: 'identity', label: 'Identity', icon: <Info className="h-4 w-4" /> },
    { id: 'channel', label: 'Channel', icon: <Cable className="h-4 w-4" /> },
    { id: 'binding', label: 'Binding', icon: <Users className="h-4 w-4" /> },
    { id: 'readiness', label: 'Readiness', icon: <CheckCircle2 className="h-4 w-4" /> },
  ];

  const primaryBinding = bindingRows.find(row => row.bindingRole === 'primary' && row.status === 'active');
  const primaryPublication = bindingPublicationOptions.find(option => option.value === primaryBinding?.publicationId);
  const summaryItems = [
    { label: 'Segment', value: form.segment.toUpperCase(), complete: true },
    { label: 'Eligibility', value: form.eligibilityMode.replace(/_/g, ' '), complete: true },
    { label: 'Channel', value: form.autoCreateChannel ? 'Auto-create' : form.channelId || 'Select one', complete: form.autoCreateChannel || Boolean(form.channelId) },
    { label: 'Default publication', value: primaryPublication?.label || (primaryBinding ? primaryBinding.publicationId : 'Configure in Binding'), complete: Boolean(primaryBinding?.publicationId) },
  ];
  const hasAccessPointDependencyWarning = dependencyWarnings.some(warning => warning.toLowerCase().includes('access point') || warning.toLowerCase().includes('pricing default'));

  return (
    <WizardWorkspace
      isOpen={isOpen}
      title={mode === 'edit' ? 'Edit Storefront' : 'New Storefront'}
      subtitle="Configure the storefront identity, channel callers, and publication routing bindings used at runtime."
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={tabId => {
        setActiveTabId(tabId);
        onActiveTabIdChange?.(tabId);
      }}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={mode === 'edit' ? 'Save Storefront' : 'Create Storefront'}
      summaryItems={summaryItems}
    >
      {activeTabId === 'identity' ? (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">Runtime Identity</h2>
            <DescriptionText>
              Define the public storefront experience key that `bff-acd` will target.
            </DescriptionText>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField label="Name" error={validationErrors.name}>
              <input className={inputCls} value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} />
            </FormField>
            <FormField label="Experience Key" error={validationErrors.experienceKey}>
              <input className={inputCls} value={form.experienceKey} onChange={event => setForm(current => ({ ...current, experienceKey: event.target.value }))} />
            </FormField>
            <FormField label="Storefront Key" error={validationErrors.storefrontKey}>
              <input className={inputCls} value={form.storefrontKey} onChange={event => setForm(current => ({ ...current, storefrontKey: event.target.value }))} placeholder="Defaults to the experience key." />
            </FormField>
            <div className="sm:col-span-2">
              <FormField label="Order confirmation redirect" error={validationErrors.confirmationRedirectUrl}>
                <input
                  className={inputCls}
                  type="url"
                  inputMode="url"
                  value={form.confirmationRedirectUrl}
                  onChange={event => setForm(current => ({ ...current, confirmationRedirectUrl: event.target.value }))}
                  placeholder="https://www.example.com/orders"
                />
                <DescriptionText className="text-xs">
                  After confirmation, customers return to this HTTPS URL with the confirmed order ID in the <code>order_id</code> query parameter.
                </DescriptionText>
              </FormField>
            </div>
            <FormField label="Market Group">
              <select className={selectCls} value={form.segment} onChange={event => setForm(current => ({ ...current, segment: event.target.value }))}>
                <option value="residential">Residential</option>
                <option value="business">Business</option>
              </select>
            </FormField>
            <FormField label="Runtime Family">
              <select className={selectCls} value={form.runtimeFamily} onChange={event => setForm(current => ({ ...current, runtimeFamily: event.target.value }))}>
                <option value="public_signup">Public Signup</option>
              </select>
            </FormField>
            <FormField label="Eligibility Type">
              <select className={selectCls} value={form.eligibilityMode} onChange={event => setForm(current => ({ ...current, eligibilityMode: event.target.value }))}>
                <option value="zone_only">Zone Only</option>
                <option value="zone_and_road_distance">Zone + Road Distance</option>
                <option value="road_distance_only">Road Distance Only</option>
              </select>
            </FormField>
            <FormField label="Route Profile">
              <select
                className={selectCls}
                value={form.routeProfile}
                disabled={form.eligibilityMode === 'zone_only'}
                onChange={event => setForm(current => ({ ...current, routeProfile: event.target.value }))}
              >
                <option value="fiber_construction">Fiber Construction</option>
              </select>
            </FormField>
            <FormField label="Status">
              <select className={selectCls} value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value }))}>
                <option value="ready">Ready</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </FormField>
          </div>
          {dependencyWarnings.length > 0 ? (
            <Callout variant="warning" className="border-amber-300/50 bg-amber-500/5">
              <VerticalStack gap={2}>
                <p className="text-sm font-black tracking-tight text-foreground">Road-Distance Dependencies Need Attention</p>
                {dependencyWarnings.map(warning => (
                  <DescriptionText key={warning} className="text-xs">
                    {warning}
                  </DescriptionText>
                ))}
                {hasAccessPointDependencyWarning && onManageAccessPoints ? (
                  <div>
                    <button
                      type="button"
                      onClick={onManageAccessPoints}
                      className="inline-flex h-9 items-center rounded-md border border-amber-400/40 bg-background px-3 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-amber-50"
                    >
                      Manage Access Points
                    </button>
                  </div>
                ) : null}
              </VerticalStack>
            </Callout>
          ) : null}
        </VerticalStack>
      ) : null}

      {activeTabId === 'channel' ? (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">Channel And Callers</h2>
            <DescriptionText>
              Pick an existing storefront channel or auto-create one, then set the allowed caller identities that should be able to use it.
            </DescriptionText>
          </div>
          <label className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/10 px-4 py-3">
            <input type="checkbox" checked={form.autoCreateChannel} onChange={event => setForm(current => ({ ...current, autoCreateChannel: event.target.checked }))} />
            <div>
              <p className="text-sm font-bold text-foreground">Auto-create channel</p>
              <DescriptionText className="text-xs">Create a storefront channel whose key matches the storefront key automatically.</DescriptionText>
            </div>
          </label>
          <FormField label="Existing Channel">
            <select
              className={selectCls}
              value={form.channelId}
              disabled={form.autoCreateChannel}
              onChange={event => setForm(current => ({ ...current, channelId: event.target.value }))}
            >
              <option value="">Select a channel</option>
              {channelOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>
          <VerticalStack gap={3}>
            <HorizontalStack className="items-center justify-between">
              <div>
                <p className="text-sm font-black tracking-tight text-foreground">Allowed Principals</p>
                <DescriptionText className="text-xs">These defaults are checked against the selected channel during readiness validation.</DescriptionText>
              </div>
              <button type="button" onClick={onAddPrincipal} className="rounded-md border border-border/60 px-4 py-2 text-xs font-bold text-foreground transition-all hover:bg-muted/40">
                Add Principal
              </button>
            </HorizontalStack>
            {principalRows.length === 0 ? (
              <EmptyState title="No principal defaults yet" body="Add the identities that should be allowed to use this storefront channel, such as oidc_client:bff-acd." />
            ) : (
              <VerticalStack gap={3}>
                {principalRows.map(row => (
                  <Surface key={row.id} className="border-border/60 bg-muted/5 p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
                      <input className={inputCls} value={row.principalType} placeholder="oidc_client" onChange={event => onChangePrincipal(row.id, { principalType: event.target.value })} />
                      <input className={inputCls} value={row.principalId} placeholder="bff-acd" onChange={event => onChangePrincipal(row.id, { principalId: event.target.value })} />
                      <button type="button" onClick={() => onRemovePrincipal(row.id)} className="rounded-md border border-destructive/30 px-4 py-2 text-xs font-bold text-destructive transition-all hover:bg-destructive/5">
                        Remove
                      </button>
                    </div>
                  </Surface>
                ))}
              </VerticalStack>
            )}
          </VerticalStack>
        </VerticalStack>
      ) : null}

      {activeTabId === 'binding' ? (
        <VerticalStack gap={6}>
          <HorizontalStack className="items-start justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-lg font-black tracking-tight">Publication Routing</h2>
              <DescriptionText>
                Keep one default publication, then select different publications for a market, market group, or trusted agent audience.
              </DescriptionText>
            </div>
            {!form.autoCreateChannel && form.channelId ? (
              <button type="button" onClick={onAddBinding} className="rounded-md border border-border/60 px-4 py-2 text-xs font-bold text-foreground transition-all hover:bg-muted/40">
                Add Route
              </button>
            ) : null}
          </HorizontalStack>
          {form.autoCreateChannel || !form.channelId ? (
            <EmptyState title="Select an existing channel" body="Create the storefront first or select its saved channel before configuring conditional publication routes." />
          ) : bindingsLoading ? (
            <DescriptionText>Loading publication routes…</DescriptionText>
          ) : (
            <VerticalStack gap={4}>
              {bindingsError ? <Callout variant="error">{bindingsError}</Callout> : null}
              {bindingRows.length === 0 ? <EmptyState title="No routes configured" body="Add one primary default publication before saving routes." /> : null}
              {bindingRows.map(row => {
                const targetOptions = row.targetType === 'market' ? marketOptions : marketGroupOptions;
                return (
                  <Surface key={row.id} className="border-border/60 bg-muted/5 p-4">
                    <VerticalStack gap={4}>
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_0.8fr_0.8fr_0.8fr_auto]">
                        <FormField label="Publication">
                          <select className={selectCls} value={row.publicationId} onChange={event => onChangeBinding(row.id, { publicationId: event.target.value })}>
                            <option value="">Select a publication</option>
                            {bindingPublicationOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                          </select>
                        </FormField>
                        <FormField label="Role">
                          <select className={selectCls} value={row.bindingRole} onChange={event => onChangeBinding(row.id, { bindingRole: event.target.value as StorefrontPublicationBindingRow['bindingRole'], targetType: event.target.value === 'primary' ? 'all' : row.targetType, audience: event.target.value === 'primary' ? 'any' : row.audience })}>
                            <option value="primary">Default</option>
                            <option value="secondary">Conditional</option>
                          </select>
                        </FormField>
                        <FormField label="Audience">
                          <select className={selectCls} value={row.audience} disabled={row.bindingRole === 'primary'} onChange={event => onChangeBinding(row.id, { audience: event.target.value as StorefrontPublicationBindingRow['audience'] })}>
                            <option value="any">Any</option>
                            <option value="public">Public</option>
                            <option value="agent">Agent</option>
                          </select>
                        </FormField>
                        <FormField label="Status">
                          <select className={selectCls} value={row.status} onChange={event => onChangeBinding(row.id, { status: event.target.value as StorefrontPublicationBindingRow['status'] })}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </FormField>
                        <div className="flex items-end"><button type="button" onClick={() => onRemoveBinding(row.id)} className="h-12 rounded-md border border-destructive/30 px-4 text-xs font-bold text-destructive hover:bg-destructive/5">Remove</button></div>
                      </div>
                      {row.bindingRole === 'secondary' ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <FormField label="Market Target">
                            <select className={selectCls} value={row.targetType} onChange={event => onChangeBinding(row.id, { targetType: event.target.value as StorefrontPublicationBindingRow['targetType'], targetId: '' })}>
                              <option value="all">All markets</option>
                              <option value="market">Direct market</option>
                              <option value="market_group">Market group</option>
                            </select>
                          </FormField>
                          <FormField label="Target">
                            <select className={selectCls} value={row.targetId} disabled={row.targetType === 'all'} onChange={event => onChangeBinding(row.id, { targetId: event.target.value })}>
                              <option value="">{row.targetType === 'all' ? 'No market target' : 'Select target'}</option>
                              {targetOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                          </FormField>
                          <FormField label="Priority">
                            <input className={inputCls} type="number" value={row.priority} onChange={event => onChangeBinding(row.id, { priority: Number(event.target.value) || 0 })} />
                          </FormField>
                        </div>
                      ) : null}
                    </VerticalStack>
                  </Surface>
                );
              })}
            </VerticalStack>
          )}
        </VerticalStack>
      ) : null}

      {activeTabId === 'readiness' ? (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">Readiness And BFF Targeting</h2>
            <DescriptionText>
              After save, use readiness to confirm the storefront is runnable and copy the experience-key snippet for `bff-acd`.
            </DescriptionText>
          </div>
          {readinessIssues.length === 0 ? (
            <EmptyState
              title={readinessStatus ? `Current status: ${readinessStatus}` : 'Save to evaluate readiness'}
              body={readinessStatus ? 'No blocking readiness issues are currently reported for this storefront.' : 'Create or save the storefront first so readiness diagnostics can run against the live records.'}
            />
          ) : (
            <VerticalStack gap={3}>
              {readinessIssues.map(issue => (
                <Surface key={`${issue.code}-${issue.message}`} className="border-border/60 bg-muted/5 p-4">
                  <VerticalStack gap={2}>
                    <p className="text-sm font-black tracking-tight text-foreground">{issue.message}</p>
                    <DescriptionText className="text-xs uppercase tracking-widest">{issue.code} • {issue.severity}</DescriptionText>
                    {issue.repair_hint ? <DescriptionText className="text-xs">{issue.repair_hint}</DescriptionText> : null}
                    {issue.code === 'road_distance_pricing_defaults_missing' && onManageAccessPoints ? (
                      <div>
                        <button
                          type="button"
                          onClick={onManageAccessPoints}
                          className="inline-flex h-9 items-center rounded-md border border-border/60 bg-background px-3 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-muted/40"
                        >
                          Manage Access Points
                        </button>
                      </div>
                    ) : null}
                  </VerticalStack>
                </Surface>
              ))}
            </VerticalStack>
          )}
          <Surface className="border-border/60 bg-muted/10 p-4">
            <VerticalStack gap={2}>
              <p className="text-sm font-black tracking-tight text-foreground">BFF Config Snippet</p>
              <pre className="overflow-x-auto rounded-md bg-background p-3 text-xs text-foreground">{configSnippet}</pre>
            </VerticalStack>
          </Surface>
        </VerticalStack>
      ) : null}
    </WizardWorkspace>
  );
}
